import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../context/AuthContext';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faSearch,
  faFilter,
  faSort,
  faEye,
  faSave,
  faTimes,
  faExchangeAlt,
  faHistory,
  faBoxes,
  faMapMarkerAlt,
  faExclamationTriangle,
  faCheckCircle,
  faWarehouse
} from '@fortawesome/free-solid-svg-icons';
import { getProducts, deleteProduct, updateProduct } from '../../services/productService';
import { getCategories } from '../../services/categoryService';
import { getInventoryLogs, logInventoryChange } from '../../services/inventoryService';
import ProductForm from './ProductForm';

const ProductsSection = () => {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name-asc');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showTransfer, setShowTransfer] = useState(null);
  const [transferData, setTransferData] = useState({ toLocation: '' });
  const [transferring, setTransferring] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        getProducts(),
        getCategories()
      ]);
      setProducts(productsData);
      setCategories([{ id: 'all', name: 'All' }, ...categoriesData]);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load products. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.categoryId === selectedCategory;
    const matchesLocation = locationFilter === 'all' || product.location === locationFilter || product.location === 'Both';
    return matchesSearch && matchesCategory && matchesLocation;
  });
  
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc': return (a.name || '').localeCompare(b.name || '');
      case 'name-desc': return (b.name || '').localeCompare(a.name || '');
      case 'price-asc': return (a.price || 0) - (b.price || 0);
      case 'price-desc': return (b.price || 0) - (a.price || 0);
      case 'stock-asc': return (a.stock || 0) - (b.stock || 0);
      case 'stock-desc': return (b.stock || 0) - (a.stock || 0);
      case 'location': return (a.location || '').localeCompare(b.location || '');
      default: return 0;
    }
  });
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'status-active';
      case 'low stock': return 'status-warning';
      case 'out of stock': return 'status-danger';
      default: return '';
    }
  };
  
  const getProductStatus = (stock) => {
    if ((stock || 0) <= 0) return 'Out of Stock';
    if ((stock || 0) <= 2) return 'Low Stock';
    return 'Active';
  };
  
  const handleDeleteProduct = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteProduct(id);
        setProducts(products.filter(product => product.id !== id));
        showSuccess(`"${name}" deleted.`);
      } catch (err) {
        console.error('Error deleting product:', err);
        let errorMessage = `Failed to delete "${name}". `;
        if (err.code === 'permission-denied') {
          errorMessage += 'You do not have permission to delete products. ' +
            'Ask an admin to grant the "manage_products" permission, ' +
            'or check that the latest Firestore rules have been deployed.';
        } else if (err.code) {
          errorMessage += `(${err.code}) ${err.message || ''}`.trim();
        } else if (err.message) {
          errorMessage += err.message;
        } else {
          errorMessage += 'Please try again.';
        }
        alert(errorMessage);
      }
    }
  };
  
  const handleEditProduct = (product) => {
    setCurrentProduct(product);
    setShowProductForm(true);
  };
  
  const handleAddProduct = () => {
    setCurrentProduct(null);
    setShowProductForm(true);
  };
  
  const handleProductFormSubmit = (updatedProducts) => {
    setProducts(updatedProducts);
    setShowProductForm(false);
    setCurrentProduct(null);
    showSuccess(currentProduct ? 'Product updated.' : 'Product added.');
  };
  
  const handleProductFormCancel = () => {
    setShowProductForm(false);
    setCurrentProduct(null);
  };
  
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  const handleStartEdit = (product) => {
    setEditingRow(product.id);
    setEditValues({
      name: product.name,
      price: product.price,
      stock: product.stock,
      categoryId: product.categoryId,
      location: product.location || 'Angola'
    });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const handleSaveEdit = async (productId) => {
    try {
      setLoading(true);
      if (!currentUser) {
        alert('You must be signed in to edit products.');
        return;
      }
      if (!editValues.name?.trim()) { alert('Product name is required'); return; }
      if (isNaN(editValues.price) || parseFloat(editValues.price) <= 0) { alert('Please enter a valid price'); return; }
      if (isNaN(editValues.stock) || parseInt(editValues.stock) < 0) { alert('Please enter a valid stock quantity'); return; }

      const oldProduct = products.find(p => p.id === productId);
      const updateData = {
        name: editValues.name.trim(),
        price: parseFloat(editValues.price),
        stock: parseInt(editValues.stock),
        categoryId: editValues.categoryId,
        location: editValues.location
      };
      
      await updateProduct(productId, updateData);

      if (oldProduct && oldProduct.stock !== updateData.stock) {
        try {
          await logInventoryChange({
            productId,
            productName: updateData.name,
            action: 'update',
            previousQuantity: oldProduct.stock || 0,
            newQuantity: updateData.stock,
            location: updateData.location,
            userId: currentUser?.uid,
            notes: `Stock updated from ${oldProduct.stock || 0} to ${updateData.stock}`
          });
        } catch (logErr) {
          console.error('Error logging inventory change:', logErr);
        }
      }

      setProducts(products.map(product => 
        product.id === productId ? { ...product, ...updateData } : product
      ));
      setEditingRow(null);
      setEditValues({});
      showSuccess(`"${updateData.name}" updated.`);
    } catch (error) {
      console.error('Error updating product:', error);
      let errorMessage = 'Failed to update product. ';
      if (error.code === 'permission-denied') errorMessage += 'You do not have permission.';
      else if (error.message) errorMessage += error.message;
      else errorMessage += 'Please try again.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditValues({ ...editValues, [field]: value });
  };

  const handleTransfer = async (product) => {
    if (!transferData.toLocation) {
      alert('Please select a destination location.');
      return;
    }
    if (transferData.toLocation === product.location) {
      alert('Product is already at that location.');
      return;
    }
    if (transferring) return;
    try {
      setTransferring(true);
      const oldLocation = product.location;
      await updateProduct(product.id, { location: transferData.toLocation });
      try {
        await logInventoryChange({
          productId: product.id,
          productName: product.name,
          action: 'transfer',
          location: oldLocation,
          toLocation: transferData.toLocation,
          previousQuantity: product.stock || 0,
          newQuantity: product.stock || 0,
          userId: currentUser?.uid,
          notes: `Moved from ${oldLocation} to ${transferData.toLocation}`
        });
      } catch (logErr) {
        console.error('Error logging transfer:', logErr);
      }
      setProducts(products.map(p => p.id === product.id ? { ...p, location: transferData.toLocation } : p));
      setShowTransfer(null);
      setTransferData({ toLocation: '' });
      showSuccess(`"${product.name}" moved to ${transferData.toLocation}.`);
    } catch (err) {
      console.error('Error transferring product:', err);
      alert('Failed to transfer. Please try again.');
    } finally {
      setTransferring(false);
    }
  };

  const handleShowLogs = async () => {
    setShowLogs(!showLogs);
    if (!showLogs) {
      setLogsLoading(true);
      try {
        const logData = await getInventoryLogs(null, 50);
        setLogs(logData);
      } catch (err) {
        console.error('Error fetching logs:', err);
      } finally {
        setLogsLoading(false);
      }
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Stats computed from products
  const stats = {
    total: products.length,
    totalStock: products.reduce((sum, p) => sum + (p.stock || 0), 0),
    lowStock: products.filter(p => (p.stock || 0) > 0 && (p.stock || 0) <= 2).length,
    outOfStock: products.filter(p => (p.stock || 0) <= 0).length,
    angola: products.filter(p => p.location === 'Angola' || p.location === 'Both').length,
    elkhart: products.filter(p => p.location === 'Elkhart' || p.location === 'Both').length
  };
  
  if (loading && products.length === 0) {
    return (
      <div className="dashboard-section products-section">
        <div className="loading-indicator">Loading products...</div>
      </div>
    );
  }
  
  return (
    <div className="dashboard-section products-section">
      <style>
        {`
          .icon-button.save { background-color: #28a745; color: white; }
          .icon-button.save:hover { background-color: #218838; }
          .icon-button.cancel { background-color: #6c757d; color: white; }
          .icon-button.cancel:hover { background-color: #5a6268; }
          .icon-button.transfer { color: #1E88E5; }
          .data-table td input, .data-table td select { border: 1px solid #ddd; border-radius: 4px; font-size: 14px; }
          .data-table td input:focus, .data-table td select:focus { outline: none; border-color: #007bff; box-shadow: 0 0 0 2px rgba(0,123,255,0.25); }
          .editing-row { background-color: #f8f9fa !important; }
          .products-stats { display: flex; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
          .products-stat-card { display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px 16px; flex: 1; min-width: 140px; }
          .products-stat-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; }
          .products-stat-details h4 { margin: 0; font-size: 12px; color: #888; font-weight: 500; }
          .products-stat-details p { margin: 0; font-size: 20px; font-weight: 700; color: #333; }
          .success-banner { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; border-radius: 6px; padding: 10px 16px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
          .error-banner { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; border-radius: 6px; padding: 10px 16px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
          .banner-close { background: none; border: none; cursor: pointer; color: inherit; margin-left: auto; }
          .transfer-inline { display: flex; align-items: center; gap: 8px; padding: 8px 0; }
          .transfer-inline select { padding: 4px 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; }
          .transfer-inline button { padding: 4px 10px; border: none; border-radius: 4px; font-size: 12px; cursor: pointer; }
          .transfer-inline .transfer-go { background: #1E88E5; color: #fff; }
          .transfer-inline .transfer-cancel { background: #eee; color: #666; }
          .logs-panel { margin-top: 20px; background: #f9f9f9; border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px; }
          .logs-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
          .logs-header h3 { margin: 0; font-size: 16px; display: flex; align-items: center; gap: 8px; }
          .log-entry { display: flex; align-items: center; gap: 12px; padding: 8px 0; border-bottom: 1px solid #eee; font-size: 13px; }
          .log-entry:last-child { border-bottom: none; }
          .log-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
          .log-badge.log-update { background: #e3f2fd; color: #1565C0; }
          .log-badge.log-transfer { background: #fff3e0; color: #E65100; }
          .log-badge.log-create { background: #e8f5e9; color: #2E7D32; }
          .log-badge.log-delete { background: #fce4ec; color: #c62828; }
          .log-badge.log-sync { background: #f3e5f5; color: #6A1B9A; }
          .log-time { margin-left: auto; color: #999; font-size: 12px; white-space: nowrap; }
          .results-count { font-size: 13px; color: #888; margin-bottom: 8px; }
        `}
      </style>

      {showProductForm ? (
        <ProductForm 
          product={currentProduct} 
          categories={categories.filter(cat => cat.id !== 'all')} 
          onSubmit={handleProductFormSubmit}
          onCancel={handleProductFormCancel}
        />
      ) : (
        <>
          {successMessage && (
            <div className="success-banner">
              <FontAwesomeIcon icon={faCheckCircle} /> {successMessage}
            </div>
          )}

          {error && (
            <div className="error-banner">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              <button onClick={() => setError(null)} className="banner-close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}

          <div className="section-header">
            <h2><FontAwesomeIcon icon={faWarehouse} /> Products & Inventory</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="secondary-button" onClick={handleShowLogs} title="View activity log">
                <FontAwesomeIcon icon={faHistory} /> Logs
              </button>
              <button className="primary-button" onClick={handleAddProduct}>
                <FontAwesomeIcon icon={faPlus} /> Add Product
              </button>
            </div>
          </div>

          <div className="products-stats">
            <div className="products-stat-card">
              <div className="products-stat-icon" style={{ backgroundColor: '#1E88E5' }}>
                <FontAwesomeIcon icon={faBoxes} />
              </div>
              <div className="products-stat-details">
                <h4>Products</h4>
                <p>{stats.total}</p>
              </div>
            </div>
            <div className="products-stat-card">
              <div className="products-stat-icon" style={{ backgroundColor: '#43A047' }}>
                <FontAwesomeIcon icon={faWarehouse} />
              </div>
              <div className="products-stat-details">
                <h4>Total Stock</h4>
                <p>{stats.totalStock}</p>
              </div>
            </div>
            <div className="products-stat-card">
              <div className="products-stat-icon" style={{ backgroundColor: '#FB8C00' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <div className="products-stat-details">
                <h4>Angola</h4>
                <p>{stats.angola}</p>
              </div>
            </div>
            <div className="products-stat-card">
              <div className="products-stat-icon" style={{ backgroundColor: '#8E24AA' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <div className="products-stat-details">
                <h4>Elkhart</h4>
                <p>{stats.elkhart}</p>
              </div>
            </div>
            {(stats.lowStock > 0 || stats.outOfStock > 0) && (
              <div className="products-stat-card">
                <div className="products-stat-icon" style={{ backgroundColor: '#F4511E' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </div>
                <div className="products-stat-details">
                  <h4>Alerts</h4>
                  <p>{stats.lowStock + stats.outOfStock}</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="filters-bar">
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-box">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option value="all">All Locations</option>
                <option value="Angola">Angola</option>
                <option value="Elkhart">Elkhart</option>
                <option value="Both">Both</option>
              </select>
            </div>

            <div className="filter-box">
              <FontAwesomeIcon icon={faFilter} />
              <select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="sort-box">
              <FontAwesomeIcon icon={faSort} />
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="price-asc">Price (Low to High)</option>
                <option value="price-desc">Price (High to Low)</option>
                <option value="stock-asc">Stock (Low to High)</option>
                <option value="stock-desc">Stock (High to Low)</option>
                <option value="location">Location</option>
              </select>
            </div>
          </div>

          <div className="results-count">
            Showing {sortedProducts.length} of {products.length} products
          </div>
          
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Product Name</th>
                  <th>Location</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProducts.length > 0 ? (
                  sortedProducts.map(product => (
                    <React.Fragment key={product.id}>
                      <tr className={editingRow === product.id ? 'editing-row' : ''}>
                        <td>
                          {product.images?.[0]?.url ? (
                            <img src={product.images[0].url} alt={product.name} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                          ) : product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                          ) : (
                            <div style={{ width: '40px', height: '40px', background: '#f5f5f5', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: '0.8rem' }}>N/A</div>
                          )}
                        </td>
                        <td>
                          {editingRow === product.id ? (
                            <input type="text" value={editValues.name || ''} onChange={(e) => handleEditChange('name', e.target.value)} style={{ width: '100%', padding: '4px' }} />
                          ) : product.name}
                        </td>
                        <td>
                          {editingRow === product.id ? (
                            <select value={editValues.location || 'Angola'} onChange={(e) => handleEditChange('location', e.target.value)} style={{ padding: '4px' }}>
                              <option value="Angola">Angola</option>
                              <option value="Elkhart">Elkhart</option>
                              <option value="Both">Both</option>
                            </select>
                          ) : (
                            <span className={`location-badge ${product.location === 'Angola' ? 'location-angola' : product.location === 'Elkhart' ? 'location-elkhart' : 'location-other'}`}>
                              {product.location || 'N/A'}
                            </span>
                          )}
                        </td>
                        <td>
                          {editingRow === product.id ? (
                            <select value={editValues.categoryId || ''} onChange={(e) => handleEditChange('categoryId', e.target.value)} style={{ width: '100%', padding: '4px' }}>
                              {categories.filter(cat => cat.id !== 'all').map(category => (
                                <option key={category.id} value={category.id}>{category.name}</option>
                              ))}
                            </select>
                          ) : getCategoryName(product.categoryId)}
                        </td>
                        <td>
                          {editingRow === product.id ? (
                            <input type="number" step="0.01" min="0" value={editValues.price || ''} onChange={(e) => handleEditChange('price', e.target.value)} style={{ width: '80px', padding: '4px' }} />
                          ) : formatCurrency(product.price)}
                        </td>
                        <td>
                          {editingRow === product.id ? (
                            <input type="number" min="0" value={editValues.stock || ''} onChange={(e) => handleEditChange('stock', e.target.value)} style={{ width: '60px', padding: '4px' }} />
                          ) : (product.stock || 0)}
                        </td>
                        <td>
                          <span className={`product-status ${getStatusClass(getProductStatus(product.stock))}`}>
                            {getProductStatus(product.stock)}
                          </span>
                        </td>
                        <td className="actions-cell">
                          {editingRow === product.id ? (
                            <>
                              <button className="icon-button save" onClick={() => handleSaveEdit(product.id)} title="Save Changes" disabled={loading}>
                                <FontAwesomeIcon icon={faSave} />
                              </button>
                              <button className="icon-button cancel" onClick={handleCancelEdit} title="Cancel Edit" disabled={loading}>
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="icon-button edit" 
                                onClick={() => handleEditProduct(product)}
                                title="Full Edit (images, description, features, etc.)"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button 
                                className="icon-button view" 
                                onClick={() => handleStartEdit(product)}
                                title="Quick Edit (name, price, stock)"
                                disabled={loading}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                className="icon-button transfer"
                                onClick={() => setShowTransfer(showTransfer === product.id ? null : product.id)}
                                title="Move to another location"
                              >
                                <FontAwesomeIcon icon={faExchangeAlt} />
                              </button>
                              <button className="icon-button delete" onClick={() => handleDeleteProduct(product.id, product.name)} title="Delete Product" disabled={loading}>
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                      {showTransfer === product.id && (
                        <tr>
                          <td colSpan="8" style={{ padding: '0 16px' }}>
                            <div className="transfer-inline">
                              <span style={{ fontSize: '13px', color: '#555' }}>Move <strong>{product.name}</strong> from {product.location || 'N/A'} to:</span>
                              <select value={transferData.toLocation} onChange={(e) => setTransferData({ toLocation: e.target.value })}>
                                <option value="">Select location</option>
                                {['Angola', 'Elkhart', 'Both'].filter(l => l !== product.location).map(loc => (
                                  <option key={loc} value={loc}>{loc}</option>
                                ))}
                              </select>
                              <button className="transfer-go" onClick={() => handleTransfer(product)} disabled={transferring}>{transferring ? 'Transferring…' : 'Transfer'}</button>
                              <button className="transfer-cancel" onClick={() => setShowTransfer(null)} disabled={transferring}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="empty-table-message">
                      No products found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {products.length === 0 && (
            <div className="empty-state">
              <p>No products have been added yet. Click "Add Product" to get started.</p>
            </div>
          )}

          {showLogs && (
            <div className="logs-panel">
              <div className="logs-header">
                <h3><FontAwesomeIcon icon={faHistory} /> Activity Log</h3>
                <button onClick={() => setShowLogs(false)} className="icon-button">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              {logsLoading ? (
                <div className="loading-indicator">Loading logs...</div>
              ) : logs.length > 0 ? (
                <div>
                  {logs.map(log => (
                    <div className="log-entry" key={log.id}>
                      <span className={`log-badge log-${log.action}`}>{log.action?.replace(/_/g, ' ')}</span>
                      <div>
                        <strong>{log.productName || 'Unknown'}</strong>
                        {log.location && <span> at {log.location}</span>}
                        {log.toLocation && <span> → {log.toLocation}</span>}
                        {log.previousQuantity !== undefined && (
                          <span> (Qty: {log.previousQuantity} → {log.newQuantity})</span>
                        )}
                      </div>
                      <span className="log-time">{formatTimestamp(log.timestamp)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#999', fontSize: '13px' }}>No activity logs yet.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductsSection;
